<template>
  <div>
    <navigation-top/>

    <div class="checkout-container">
      <div class="card plan-info">
        <p class="plan-name">Qlerify {{ plan?.name }} plan</p>

        <div class="divider"></div>

        <div class="point-container">
          <p class="point-title"><span class="point">1</span> Billing cycle</p>

          <div class="subscription-types">
            <v-radio-group v-model="localSubscriptionType" inline>
              <v-radio label="Yearly" value="yearly">
                <template v-slot:label>
                  <div>Yearly <span class="save-badge">Save 17%</span></div>
                </template>
              </v-radio>
              <v-radio label="Monthly" value="monthly"/>
            </v-radio-group>
          </div>
        </div>

        <div class="divider"></div>

        <div class="point-container">
          <p class="point-title"><span class="point">2</span> Add users to your plan</p>
          <p class="point-description">
            Enter the email addresses of the people you want to add to your plan.
          </p>
          <div class="users-container">
            <p class="users-title">Users ({{ usersCount }}):</p>

            <div class="users-list">
              <div
                v-for="user in users"
                :key="user.email"
                class="users-list-item"
                :class="{isMe: user.email === currentUser.email}"
              >
                <span class="user-email">{{ user.email }}  <strong v-if="user.email === currentUser.email">(You)</strong></span>
                <div class="user-actions">
                  <div v-if="user.isPro" class="pro-user-badge">
                    Pro User
                  </div>
                  <div class="remove-user-action" @click="removeUser(user.email)" v-if="user.email !== currentUser.email">
                    <v-icon>mdi-close</v-icon>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div class="mb-4">
                <add-subscription-user-field
                  :modelValue="emailInputValue"
                  :emails="emails"
                  @update:modelValue="(value) => emailInputValue = value"
                  @update-emails="updateEmails"
                  :disabled="loadingValidateUsersSubscriptions"
                />
              </div>
              <div class="plan-badge d-inline-flex mb-2">
                You can add or remove members to your plan at anytime
              </div>
              <div class="d-flex justify-end">
                <v-btn
                  size="large"
                  variant="flat"
                  :disabled="!emailsAreValid"
                  :loading="loadingValidateUsersSubscriptions"
                  @click="addNewUser"
                >
                  Add {{ emails.length ? emails.length : '' }} new member
                </v-btn>
              </div>
            </div>

          </div>
        </div>
      </div>
      <div>
        <div class="card order-summary mb-10">
          <p class="order-summary-title">Order summary</p>

          <div class="divider smallSpace"></div>

          <p class="order-summary-subscription-type">Qlerify {{ plan?.name }} Plan</p>

          <div class="d-flex justify-space-between">
            <p class="order">{{ orderCalculation }}</p>
            <p class="orderPrice">US$ {{ totalPrice?.toFixed(2) }}</p>
          </div>

          <div class="divider smallSpace"></div>

          <div class="d-flex justify-space-between mb-10">
            <p class="order">
              <template v-if="plan?.name === 'Pro' && !activeSubscription?.lastSubscriptionDetails">
                <strong>Due in 28 days</strong> (Card required, no charge for 4 weeks, cancel anytime)
              </template>
              <template v-else>
                <strong>Due today</strong>
              </template>
            </p>
            <p class="orderPrice"><strong>US$ {{ totalPrice }}</strong></p>
          </div>

          <div class="d-flex justify-center">
            <v-btn
              size="large"
              variant="flat"
              @click="goToPayment"
            >
              Continue to card details
            </v-btn>
          </div>
        </div>

        <div v-if="plan?.name === 'Pro' && !activeSubscription?.lastSubscriptionDetails">
          <p class="text-h4">How the trial works</p>
          <hr class="mt-5 mb-5">
          <p><strong>Today - Your 4 week free trial starts</strong></p>
          <p>You and all the people you invited get instant access to all Qlerify Pro features.</p>
          <br>
          <p><strong>
            {{ format(add(new Date(), {weeks: 4}), 'MMMM d, yyyy') }} - Your new subscription starts</strong>
          </p>
          <p>
            Unless the trial is cancelled, your Pro plan will automatically start, charging you US$ {{ totalPrice }}
            {{ subscriptionType }} for {{ usersCount }} {{ usersCount === 1 ? 'user' : 'users' }}.
          </p>
        </div>
      </div>
    </div>

    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />
  </div>
</template>

<script>
import add from 'date-fns/add'
import format from 'date-fns/format'
import store from '@/store'
import { validateEmail } from '@/utils/validate'
import NavigationTop from '../components/NavigationTop.vue'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'
import AddSubscriptionUserField from '@/components/AddSubscriptionUserField.vue'
import { mapState } from 'vuex'

export default {
  components: {
    AddSubscriptionUserField,
    InfoMessageDialog,
    NavigationTop
  },
  computed: {
    ...mapState({
      plan: state => state.payments.plan,
      subscriptionType: state => state.payments.subscriptionType,
      users: state => state.payments.users,
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      loadingValidateUsersSubscriptions: state => state.payments.loadingValidateUsersSubscriptions
    }),
    usersCount: function () {
      return this.users.length
    },
    emailsAreValid: function () {
      if (this.emails.length > 0) {
        return true
      }

      const emailsArray = Array.from(new Set((this.emailInputValue || '').split(',').map((e) => e.trim())))
      const validEmails = emailsArray.filter((email) => !!validateEmail(email) && !this.emails.includes(email))
      return validEmails.length !== 0
    },
    orderCalculation: function () {
      let str = 'US$'
      str += this.plan?.value === 'basic' ? (this?.subscriptionType === 'yearly' ? '250' : '25') : this.plan?.value === 'pro' ? (this.subscriptionType === 'yearly' ? '400' : '40') : '0'
      str += ` x ${this.usersCount} ${this.usersCount === 1 ? 'user' : 'users'}`
      str += ' 1 x '
      str += this.subscriptionType === 'yearly' ? 'year' : 'month'
      return str
    },
    totalPrice: function () {
      const price = this.plan?.value === 'basic' ? (this.subscriptionType === 'yearly' ? 250 : 25) : this.plan?.value === 'pro' ? (this.subscriptionType === 'yearly' ? 400 : 40) : '0'
      const totalPrice = price * this.usersCount
      store.commit('payments/setTotalPrice', totalPrice)
      return totalPrice
    }
  },
  methods: {
    format,
    add,
    async addNewUser () {
      if (!this.emails.length) {
        return
      }

      const usersToAdd = this.emails.map(email => ({ email }))
      const response = await store.dispatch('payments/validateUsersSubscriptions', { users: usersToAdd })

      if (response.success && response.users.length > 0) {
        const usersWithSubscription = []
        response.users.forEach((user) => {
          if (user.status === 'active') {
            usersWithSubscription.push(user)
          } else {
            const alreadyExists = this.users.findIndex(u => u.email === user.email) !== -1
            if (alreadyExists) {
              return
            }
            const newUser = { email: user.email }
            store.commit('payments/addNewUser', newUser)
          }
        })

        if (usersWithSubscription.length > 0) {
          const commaSeparatedEmails = usersWithSubscription.map(user => user.email).join(', ')
          const isSingleUser = usersWithSubscription.length === 1

          const message = isSingleUser
            ? `The user with email ${commaSeparatedEmails} already has an active subscription. You don’t need to add them to your plan. Instead, leave this page and invite them directly to the project you want them to access.`
            : `The users with emails ${commaSeparatedEmails} already have active subscriptions. You don’t need to add them to your plan. Instead, leave this page and invite them directly to the projects you want them to access.`

          this.infoMessageDialogData = {
            message,
            isOpen: true
          }
        }
      }

      this.emailInputValue = ''
      this.emails = []
    },
    removeUser (email) {
      store.commit('payments/removeUserByEmail', email)
    },
    updateEmails (emails) {
      this.emails = emails
    },
    goToPayment () {
      this.$router.push('/payment')
    }
  },
  data () {
    return {
      emails: [],
      emailInputValue: '',
      localSubscriptionType: 'monthly',
      infoMessageDialogData: null
    }
  },
  mounted () {
    if (!this.subscriptionType || !this.plan) {
      return this.$router.push('/plans')
    }
    this.localSubscriptionType = this.subscriptionType
    if (this.currentUser) {
      const me = { email: this.currentUser.email }
      store.commit('payments/addNewUser', me)
    }
  },
  watch: {
    localSubscriptionType: function (subscriptionType) {
      store.commit('payments/setSubscriptionType', subscriptionType)
    }
  }
}
</script>

<style scoped>
.checkout-container {
  gap: 48px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.card {
  padding: 24px !important;
  background-color: var(--white, #ffffff);
}

.plan-info {
  width: 636px;
}

.order-summary {
  width: 581px;
}

.plan-name {
  font-size: 20px;
  font-weight: 400;
  color: #000000;
}

.divider {
  width: 100%;
  height: 1px;
  margin: 32px 0;
  background-color: var(--dark-1);

  &.smallSpace {
    margin: 20px 0;
  }
}

.point-title {
  gap: 8px;
  display: flex;
  align-self: center;
  font-size: 16px;
  font-weight: 400;
  color: #000000;
}

.point {
  font-size: 20px;
  font-weight: 400;
  color: #000000;
}

.point-description {
  font-size: 14px;
  font-weight: 300;
  color: #000000;
  margin-top: 16px;
}

.subscription-types {
  padding-top: 32px;
  margin-bottom: -22px;
}

.save-badge {
  font-size: 12px;
  font-weight: 400;
  color: #000000;
  padding: 6px 12px;
  margin-left: 12px;
  margin-right: 24px;
  border-radius: 21px;
  background: #bdf5d4;
}

.users-container {
  margin-top: 40px;
}

.users-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--dark-3, #61676E);
  margin-bottom: 24px;
}

.users-list {
  display: flex;
  margin-bottom: 24px;
  flex-direction: column;
}

.users-list-item {
  gap: 8px;
  height: 34px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  align-items: center;
  align-self: stretch;
  padding: 6px 8px 8px 8px;
  justify-content: space-between;
  color: var(--dark-4, #363A40);
  border-bottom: 1px solid var(--light-3, #E5E3E1);

  &:first-child {
    border-top: 1px solid var(--light-3, #E5E3E1);
  }
}

.user-email {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  color: var(--dark-4, #363A40);
}

.user-actions {
  gap: 8px;
  display: flex;
  align-items: center;
}

.pro-user-badge {
  height: 20px;
  display: flex;
  padding: 0 8px;
  align-items: center;
  border-radius: 14px;
  background: var(--blue-3);
  justify-content: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  color: var(--light-1, #FCFCFC);
}

.remove-user-action {
  cursor: pointer;
}

.order-summary-title {
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  color: var(--text-dark, #1D1F22);
}

.order-summary-subscription-type {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  color: var(--text-dark, #1D1F22);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}
</style>
